// 編集履歴の action_type を子ども向けの文言＋アイコンに変換する表示専用ヘルパー。
// 文言は i18n（MSG_BUILDER_HISTORY_ACTION_* / MSG_BUILDER_HISTORY_EVENT_*）に定義。未知の action_type は raw 文字列でフォールバック。
// 記録と undo / redo の決まりは docs/edit-history-undo.md
import I18n from "common/i18n";
import { PieceMilestone, PieceEditHistoryEntry } from "features/builder/types";

// action_type → { i18n キー, material-symbols アイコン名 }
const HISTORY_ACTION_MAP: Record<string, { labelKey: string; icon: string }> = {
  place_asset: { labelKey: "MSG_BUILDER_HISTORY_ACTION_PLACE_ASSET", icon: "add" },
  remove_asset: { labelKey: "MSG_BUILDER_HISTORY_ACTION_REMOVE_ASSET", icon: "delete" },
  change_asset: { labelKey: "MSG_BUILDER_HISTORY_ACTION_CHANGE_ASSET", icon: "edit" },
  resize_map: { labelKey: "MSG_BUILDER_HISTORY_ACTION_RESIZE_MAP", icon: "aspect_ratio" },
  set_goal: { labelKey: "MSG_BUILDER_HISTORY_ACTION_SET_GOAL", icon: "flag" },
  set_character: { labelKey: "MSG_BUILDER_HISTORY_ACTION_SET_CHARACTER", icon: "person" },
  // AI でつくった・restore は区切り。undo / redo で「もとにもどした：AIで つくった」と書くのに使う
  ai_generate: { labelKey: "MSG_BUILDER_HISTORY_ACTION_AI_GENERATE", icon: "auto_awesome" },
  restore: { labelKey: "MSG_BUILDER_HISTORY_ACTION_RESTORE", icon: "history" },
  // くわしく・つかえる コマンドで変えたもの
  rename: { labelKey: "MSG_BUILDER_HISTORY_ACTION_RENAME", icon: "edit" },
  change_description: { labelKey: "MSG_BUILDER_HISTORY_ACTION_CHANGE_DESC", icon: "notes" },
  change_hints: { labelKey: "MSG_BUILDER_HISTORY_ACTION_CHANGE_HINTS", icon: "lightbulb" },
  change_cover: { labelKey: "MSG_BUILDER_HISTORY_ACTION_CHANGE_COVER", icon: "image" },
  change_background: {
    labelKey: "MSG_BUILDER_HISTORY_ACTION_CHANGE_BACKGROUND",
    icon: "wallpaper",
  },
  change_commands: { labelKey: "MSG_BUILDER_HISTORY_ACTION_CHANGE_COMMANDS", icon: "extension" },
};

const actionLabel = (actionType: string): string => {
  const entry = HISTORY_ACTION_MAP[actionType];
  return entry ? I18n.t(entry.labelKey) : actionType;
};

// 履歴 1 行に表示するラベルとアイコンを返す。未知の type は type 文字列をそのまま表示。
// undo / redo は、何を取り消した・やり直したかを添える（「もとにもどした：ブロックを おいた」）
export const resolveHistoryLabel = (
  entry: PieceEditHistoryEntry,
): { label: string; icon: string } => {
  if (entry.action_type === "undo" || entry.action_type === "redo") {
    const undone = entry.payload?.undone;
    const isUndo = entry.action_type === "undo";
    return {
      label:
        typeof undone === "string"
          ? I18n.t(
              isUndo ? "MSG_BUILDER_HISTORY_ACTION_UNDO_OF" : "MSG_BUILDER_HISTORY_ACTION_REDO_OF",
              {
                action: actionLabel(undone),
              },
            )
          : I18n.t(isUndo ? "MSG_BUILDER_HISTORY_ACTION_UNDO" : "MSG_BUILDER_HISTORY_ACTION_REDO"),
      icon: isUndo ? "undo" : "redo",
    };
  }
  const mapped = HISTORY_ACTION_MAP[entry.action_type];
  if (!mapped) return { label: entry.action_type, icon: "history" };
  return { label: I18n.t(mapped.labelKey), icon: mapped.icon };
};

// 区切り（セーブポイント）。りれきではカードにして、その下にそこまでにした操作を並べる。
// badge = アイコンの座の色（Tailwind JIT が拾えるようリテラルで保持。色はじゆうにつくるの区切りと同じ使い分け）
const MILESTONE_MAP: Record<string, { labelKey: string; icon: string; badge: string }> = {
  [PieceMilestone.OPEN]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_OPEN",
    icon: "edit_document",
    badge: "bg-blue-100",
  },
  [PieceMilestone.CLOSE]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_CLOSE",
    icon: "save",
    badge: "bg-blue-100",
  },
  [PieceMilestone.TEST]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_TEST",
    icon: "play_arrow",
    badge: "bg-green-100",
  },
  [PieceMilestone.AI_GENERATE]: {
    labelKey: "MSG_BUILDER_HISTORY_ACTION_AI_GENERATE",
    icon: "auto_awesome",
    badge: "bg-orange-100",
  },
  [PieceMilestone.AUTO]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_AUTO",
    icon: "schedule",
    badge: "bg-blue-100",
  },
  [PieceMilestone.COMPLETE]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_COMPLETE",
    icon: "check_circle",
    badge: "bg-green-100",
  },
  [PieceMilestone.PUBLISH]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_PUBLISH",
    icon: "public",
    badge: "bg-orange-100",
  },
  [PieceMilestone.UNPUBLISH]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_UNPUBLISH",
    icon: "lock",
    badge: "bg-blue-100",
  },
  [PieceMilestone.BEFORE_RESTORE]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_BEFORE_RESTORE",
    icon: "history",
    badge: "bg-blue-100",
  },
  [PieceMilestone.RESTORE]: {
    labelKey: "MSG_BUILDER_HISTORY_ACTION_RESTORE",
    icon: "history",
    badge: "bg-orange-100",
  },
  [PieceMilestone.DISCARD]: {
    labelKey: "MSG_BUILDER_HISTORY_EVENT_DISCARD",
    icon: "delete_history",
    badge: "bg-orange-100",
  },
};

// 区切りか判定。
export const isMilestoneAction = (actionType: string): boolean => actionType in MILESTONE_MAP;

// 区切りのカードに出すラベル・アイコン・座の色を返す
export const resolveMilestone = (
  entry: PieceEditHistoryEntry,
): { label: string; icon: string; badge: string } => {
  const m = MILESTONE_MAP[entry.action_type];
  return { label: I18n.t(m.labelKey), icon: m.icon, badge: m.badge };
};
